/**
 * Trovva — Data Access Layer
 * All Supabase CRUD operations in one place.
 * Every function returns { data, error } from Supabase.
 */

import { supabase } from './supabase';

// ============================================================
// ORGANIZATION
// ============================================================

export async function getOrganization() {
  const { data, error } = await supabase
    .from('organizations')
    .select('*')
    .single();
  return { data, error };
}

// ============================================================
// USERS / TEAM
// ============================================================

export async function getCurrentUser() {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: 'Not authenticated' };

  const { data, error } = await supabase
    .from('users')
    .select('*, organizations(*)')
    .eq('id', user.id)
    .single();
  return { data, error };
}

export async function getTeamMembers() {
  const { data, error } = await supabase
    .from('users')
    .select('id, full_name, email, phone, role, avatar_url, is_active, specialties')
    .eq('is_active', true)
    .order('full_name');
  return { data, error };
}

// ============================================================
// CONTACTS / LEADS
// ============================================================

export async function getLeads(filters = {}) {
  let query = supabase
    .from('contacts')
    .select(`
      *,
      assigned_agent:users!contacts_assigned_to_fkey(id, full_name, phone, email)
    `)
    .not('lead_status', 'is', null)
    .order('created_at', { ascending: false });

  if (filters.status && filters.status !== 'all') {
    query = query.eq('lead_status', filters.status);
  }
  if (filters.type && filters.type !== 'all') {
    query = query.eq('lead_type', filters.type);
  }
  if (filters.source && filters.source !== 'all') {
    query = query.eq('lead_source', filters.source);
  }
  if (filters.assigned_to) {
    query = query.eq('assigned_to', filters.assigned_to);
  }
  if (filters.search) {
    query = query.or(
      `first_name.ilike.%${filters.search}%,last_name.ilike.%${filters.search}%,email.ilike.%${filters.search}%,phone.ilike.%${filters.search}%`
    );
  }

  const { data, error } = await query;
  return { data, error };
}

export async function getLeadById(id) {
  const { data, error } = await supabase
    .from('contacts')
    .select(`
      *,
      assigned_agent:users!contacts_assigned_to_fkey(id, full_name, phone, email),
      communications(id, channel, direction, subject, body, created_at, ai_generated),
      deals(id, deal_type, status, offer_price, sale_price, closing_date)
    `)
    .eq('id', id)
    .single();
  return { data, error };
}

export async function createLead(leadData) {
  // Get current user's org_id
  const { data: user } = await getCurrentUser();
  if (!user) return { data: null, error: 'Not authenticated' };

  const { data, error } = await supabase
    .from('contacts')
    .insert({
      org_id: user.org_id,
      first_name: leadData.first_name,
      last_name: leadData.last_name,
      email: leadData.email || null,
      phone: leadData.phone || null,
      lead_status: 'new',
      lead_type: leadData.lead_type,
      lead_source: leadData.lead_source || null,
      assigned_to: leadData.assigned_to || null,
      budget: leadData.budget || null,
      timeline: leadData.timeline || null,
      notes: leadData.notes || null,
      roles: [leadData.lead_type === 'renter' ? 'renter' : leadData.lead_type === 'pm_owner' ? 'owner' : 'buyer'],
      tags: leadData.tags || [],
    })
    .select()
    .single();
  return { data, error };
}

export async function updateLead(id, updates) {
  const { data, error } = await supabase
    .from('contacts')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  return { data, error };
}

export async function updateLeadStatus(id, newStatus) {
  return updateLead(id, { lead_status: newStatus });
}

export async function assignLead(leadId, agentId) {
  const updates = {
    assigned_to: agentId,
    lead_status: 'contacted',  // Auto-advance from 'new' when assigned
  };
  return updateLead(leadId, updates);
}

export async function deleteLead(id) {
  const { data, error } = await supabase
    .from('contacts')
    .delete()
    .eq('id', id);
  return { data, error };
}

// ============================================================
// LEAD PIPELINE STATS
// ============================================================

export async function getLeadPipelineStats() {
  const { data, error } = await supabase
    .from('contacts')
    .select('lead_status, lead_type')
    .not('lead_status', 'is', null)
    .not('lead_status', 'in', '("closed","lost")');

  if (error) return { data: null, error };

  const stats = {
    total: data.length,
    new: data.filter(l => l.lead_status === 'new').length,
    contacted: data.filter(l => l.lead_status === 'contacted').length,
    qualified: data.filter(l => l.lead_status === 'qualified').length,
    nurturing: data.filter(l => l.lead_status === 'nurturing').length,
    under_contract: data.filter(l => l.lead_status === 'under_contract').length,
    byType: {
      buyer: data.filter(l => l.lead_type === 'buyer').length,
      seller: data.filter(l => l.lead_type === 'seller').length,
      renter: data.filter(l => l.lead_type === 'renter').length,
      commercial: data.filter(l => l.lead_type === 'commercial').length,
      pm_owner: data.filter(l => l.lead_type === 'pm_owner').length,
    },
  };
  return { data: stats, error: null };
}

// ============================================================
// PROPERTIES
// ============================================================

export async function getProperties(filters = {}) {
  let query = supabase
    .from('properties')
    .select(`
      *,
      owner:contacts!properties_owner_id_fkey(id, first_name, last_name, phone, email),
      tenant:contacts!properties_current_tenant_id_fkey(id, first_name, last_name, phone, email)
    `)
    .order('address');

  if (filters.is_managed !== undefined) {
    query = query.eq('is_managed', filters.is_managed);
  }
  if (filters.listing_status) {
    query = query.eq('listing_status', filters.listing_status);
  }

  const { data, error } = await query;
  return { data, error };
}

// ============================================================
// MAINTENANCE REQUESTS
// ============================================================

export async function getMaintenanceRequests(filters = {}) {
  let query = supabase
    .from('maintenance_requests')
    .select(`
      *,
      property:properties(id, address, unit, city),
      tenant:contacts!maintenance_requests_tenant_id_fkey(id, first_name, last_name, phone),
      owner:contacts!maintenance_requests_owner_id_fkey(id, first_name, last_name, phone, owner_repair_threshold),
      assigned_vendor:contacts!maintenance_requests_assigned_vendor_id_fkey(id, first_name, last_name, vendor_company, phone),
      vendor_quotes(*)
    `)
    .order('created_at', { ascending: false });

  if (filters.status && filters.status !== 'all') {
    query = query.eq('status', filters.status);
  }
  if (filters.priority && filters.priority !== 'all') {
    query = query.eq('priority', filters.priority);
  }
  if (filters.property_id) {
    query = query.eq('property_id', filters.property_id);
  }

  const { data, error } = await query;
  return { data, error };
}

// ============================================================
// DEALS / TRANSACTIONS
// ============================================================

export async function getDeals(filters = {}) {
  let query = supabase
    .from('deals')
    .select(`
      *,
      property:properties(id, address, unit, city, listing_price),
      client:contacts!deals_contact_id_fkey(id, first_name, last_name, phone, email),
      agent:users!deals_agent_id_fkey(id, full_name, phone),
      milestones:deal_milestones(*)
    `)
    .order('created_at', { ascending: false });

  if (filters.status && filters.status !== 'all') {
    query = query.eq('status', filters.status);
  }
  if (filters.deal_type && filters.deal_type !== 'all') {
    query = query.eq('deal_type', filters.deal_type);
  }

  const { data, error } = await query;
  return { data, error };
}

// ============================================================
// FINANCIAL TRANSACTIONS
// ============================================================

export async function getFinancialTransactions(filters = {}) {
  let query = supabase
    .from('financial_transactions')
    .select(`
      *,
      property:properties(id, address, unit),
      contact:contacts(id, first_name, last_name),
      deal:deals(id, deal_type),
      agent:users(id, full_name)
    `)
    .order('date', { ascending: false });

  if (filters.entity && filters.entity !== 'all') {
    query = query.eq('entity', filters.entity);
  }
  if (filters.transaction_type) {
    query = query.eq('transaction_type', filters.transaction_type);
  }
  if (filters.reconciled !== undefined) {
    query = query.eq('reconciled', filters.reconciled);
  }
  if (filters.startDate) {
    query = query.gte('date', filters.startDate);
  }
  if (filters.endDate) {
    query = query.lte('date', filters.endDate);
  }

  const { data, error } = await query;
  return { data, error };
}

// ============================================================
// COMMUNICATIONS
// ============================================================

export async function logCommunication(commData) {
  const { data: user } = await getCurrentUser();
  if (!user) return { data: null, error: 'Not authenticated' };

  const { data, error } = await supabase
    .from('communications')
    .insert({
      org_id: user.org_id,
      contact_id: commData.contact_id,
      user_id: commData.ai_generated ? null : user.id,
      channel: commData.channel,
      direction: commData.direction || 'outbound',
      subject: commData.subject || null,
      body: commData.body,
      ai_generated: commData.ai_generated || false,
      deal_id: commData.deal_id || null,
      maintenance_id: commData.maintenance_id || null,
      property_id: commData.property_id || null,
    })
    .select()
    .single();
  return { data, error };
}

// ============================================================
// AI ACTIVITY LOG
// ============================================================

export async function logAiActivity(activityData) {
  const { data: user } = await getCurrentUser();
  if (!user) return { data: null, error: 'Not authenticated' };

  const { data, error } = await supabase
    .from('ai_activity_log')
    .insert({
      org_id: user.org_id,
      action_type: activityData.action_type,
      description: activityData.description,
      details: activityData.details || {},
      contact_id: activityData.contact_id || null,
      property_id: activityData.property_id || null,
      deal_id: activityData.deal_id || null,
      maintenance_id: activityData.maintenance_id || null,
      requires_approval: activityData.requires_approval || false,
      confidence: activityData.confidence || null,
    })
    .select()
    .single();
  return { data, error };
}

export async function getRecentAiActivity(limit = 20) {
  const { data, error } = await supabase
    .from('ai_activity_log')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);
  return { data, error };
}

// ============================================================
// NOTIFICATIONS
// ============================================================

export async function getNotifications(unreadOnly = true) {
  let query = supabase
    .from('notifications')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(50);

  if (unreadOnly) {
    query = query.is('read_at', null);
  }

  const { data, error } = await query;
  return { data, error };
}

export async function markNotificationRead(id) {
  const { data, error } = await supabase
    .from('notifications')
    .update({ read_at: new Date().toISOString() })
    .eq('id', id);
  return { data, error };
}

export async function createNotification(notifData) {
  const { data: user } = await getCurrentUser();
  if (!user) return { data: null, error: 'Not authenticated' };

  const { data, error } = await supabase
    .from('notifications')
    .insert({
      org_id: user.org_id,
      user_id: notifData.user_id || user.id,
      title: notifData.title,
      body: notifData.body || null,
      notification_type: notifData.notification_type,
      priority: notifData.priority || 'normal',
      contact_id: notifData.contact_id || null,
      property_id: notifData.property_id || null,
      deal_id: notifData.deal_id || null,
      maintenance_id: notifData.maintenance_id || null,
    })
    .select()
    .single();
  return { data, error };
}

// ============================================================
// LEASES
// ============================================================

export async function getLeases(filters = {}) {
  let query = supabase
    .from('leases')
    .select(`
      *,
      property:properties(id, address, unit, city, state),
      tenant:contacts!leases_tenant_id_fkey(id, first_name, last_name, phone, email),
      owner:contacts!leases_owner_id_fkey(id, first_name, last_name, phone, email)
    `)
    .order('lease_end', { ascending: true });

  if (filters.renewal_status && filters.renewal_status !== 'all') {
    query = query.eq('renewal_status', filters.renewal_status);
  }
  if (filters.property_id) {
    query = query.eq('property_id', filters.property_id);
  }
  if (filters.expiring_within_days) {
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + filters.expiring_within_days);
    query = query
      .lte('lease_end', futureDate.toISOString().split('T')[0])
      .gte('lease_end', new Date().toISOString().split('T')[0])
      .not('renewal_status', 'in', '("renewed","expired")');
  }

  const { data, error } = await query;
  return { data, error };
}

export async function updateLease(id, updates) {
  const { data, error } = await supabase
    .from('leases')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  return { data, error };
}

export async function createLease(leaseData) {
  const { data: user } = await getCurrentUser();
  if (!user) return { data: null, error: 'Not authenticated' };

  const { data, error } = await supabase
    .from('leases')
    .insert({
      org_id: user.org_id,
      property_id: leaseData.property_id,
      tenant_id: leaseData.tenant_id,
      owner_id: leaseData.owner_id || null,
      lease_start: leaseData.lease_start,
      lease_end: leaseData.lease_end,
      monthly_rent: leaseData.monthly_rent,
      security_deposit: leaseData.security_deposit || null,
      pet_deposit: leaseData.pet_deposit || null,
      lease_type: leaseData.lease_type || 'fixed',
      renewal_status: 'active',
    })
    .select()
    .single();
  return { data, error };
}

// ============================================================
// PM STATS (Property Management Overview)
// ============================================================

export async function getPMStats() {
  const [properties, leases, maintenance, financials] = await Promise.all([
    supabase.from('properties')
      .select('id, address, unit, city, is_managed, is_vacant, current_rent, management_fee_pct, owner_id, current_tenant_id')
      .eq('is_managed', true),
    supabase.from('leases')
      .select('id, lease_end, monthly_rent, renewal_status, property_id, tenant_id')
      .not('renewal_status', 'in', '("renewed","expired")'),
    supabase.from('maintenance_requests')
      .select('id, property_id, status, priority')
      .not('status', 'in', '("completed","verified","billed","cancelled")'),
    supabase.from('financial_transactions')
      .select('amount, transaction_type, property_id, date')
      .eq('entity', 'pm')
      .gte('date', new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]),
  ]);

  const managedProps = properties.data || [];
  const totalUnits = managedProps.length;
  const occupiedUnits = managedProps.filter(p => !p.is_vacant && p.current_tenant_id).length;
  const vacantUnits = totalUnits - occupiedUnits;
  const totalRent = managedProps.reduce((sum, p) => sum + (Number(p.current_rent) || 0), 0);
  const totalMgmtFee = managedProps.reduce((sum, p) => sum + ((Number(p.current_rent) || 0) * (Number(p.management_fee_pct) || 0) / 100), 0);

  const now = new Date();
  const sixtyDays = new Date();
  sixtyDays.setDate(now.getDate() + 60);
  const expiringLeases = (leases.data || []).filter(l => {
    const end = new Date(l.lease_end);
    return end >= now && end <= sixtyDays;
  }).length;

  const openMaint = (maintenance.data || []).length;

  const rentCollected = (financials.data || [])
    .filter(f => f.transaction_type === 'rent_collected')
    .reduce((sum, f) => sum + Number(f.amount), 0);

  return {
    totalUnits, occupiedUnits, vacantUnits, totalRent, totalMgmtFee,
    expiringLeases, openMaint, rentCollected,
    occupancyRate: totalUnits > 0 ? Math.round((occupiedUnits / totalUnits) * 100) : 0,
  };
}

// ============================================================
// RENT ROLL
// ============================================================

export async function getRentRoll(month, year) {
  // Get all managed properties with tenant info
  const { data: properties, error } = await supabase
    .from('properties')
    .select(`
      id, address, unit, city, current_rent, is_vacant, is_managed,
      current_tenant:contacts!properties_current_tenant_id_fkey(id, first_name, last_name, phone, email)
    `)
    .eq('is_managed', true)
    .order('address');

  if (error) return { data: null, error };

  // Get rent payments for this month
  const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
  const endDate = month === 12
    ? `${year + 1}-01-01`
    : `${year}-${String(month + 1).padStart(2, '0')}-01`;

  const { data: payments } = await supabase
    .from('financial_transactions')
    .select('property_id, amount, date, contact_id')
    .eq('transaction_type', 'rent_collected')
    .gte('date', startDate)
    .lt('date', endDate);

  // Merge properties with payment status
  const rentRoll = (properties || []).map(prop => {
    const payment = (payments || []).find(p => p.property_id === prop.id);
    const tenantName = prop.current_tenant
      ? `${prop.current_tenant.first_name} ${prop.current_tenant.last_name}`
      : null;

    return {
      property_id: prop.id,
      property: prop.address + (prop.unit ? `, ${prop.unit}` : ''),
      tenant: tenantName,
      rent: Number(prop.current_rent) || 0,
      status: prop.is_vacant ? 'vacant' : payment ? 'paid' : 'overdue',
      paid_date: payment?.date || null,
      balance: prop.is_vacant ? 0 : payment ? 0 : (Number(prop.current_rent) || 0),
    };
  });

  return { data: rentRoll, error: null };
}

// ============================================================
// LEASE RENEWAL AUTOMATION
// ============================================================

/**
 * Get all leases expiring within a given number of days.
 * Returns leases with property, tenant, and owner info.
 */
export async function getExpiringLeases(withinDays = 120) {
  const today = new Date().toISOString().split('T')[0];
  const futureDate = new Date();
  futureDate.setDate(futureDate.getDate() + withinDays);
  const futureDateStr = futureDate.toISOString().split('T')[0];

  const { data, error } = await supabase
    .from('leases')
    .select(`
      *,
      property:properties(id, address, unit, city, state),
      tenant:contacts!leases_tenant_id_fkey(id, first_name, last_name, phone, email),
      owner:contacts!leases_owner_id_fkey(id, first_name, last_name, phone, email)
    `)
    .gte('lease_end', today)
    .lte('lease_end', futureDateStr)
    .not('renewal_status', 'in', '("renewed","expired")')
    .order('lease_end', { ascending: true });

  if (error) return { data: null, error };

  // Enrich with computed fields
  const enriched = (data || []).map(lease => {
    const daysUntilExpiry = Math.ceil(
      (new Date(lease.lease_end) - new Date()) / (1000 * 60 * 60 * 24)
    );
    let urgency = 'normal';
    if (daysUntilExpiry <= 30) urgency = 'critical';
    else if (daysUntilExpiry <= 60) urgency = 'warning';
    else if (daysUntilExpiry <= 90) urgency = 'upcoming';

    return { ...lease, daysUntilExpiry, urgency };
  });

  return { data: enriched, error: null };
}

/**
 * Run the lease renewal check.
 * Updates renewal_status based on days until expiry,
 * creates notifications, and returns actions taken.
 */
export async function runLeaseRenewalCheck() {
  const { data: leases, error } = await getExpiringLeases(120);
  if (error || !leases) return { actions: [], error };

  const actions = [];

  for (const lease of leases) {
    const currentStatus = lease.renewal_status;
    let newStatus = null;

    // Determine if status needs to advance based on days until expiry
    if (lease.daysUntilExpiry <= 30 && !['reminder_30', 'renewal_sent', 'negotiating', 'renewed', 'not_renewing'].includes(currentStatus)) {
      newStatus = 'reminder_30';
    } else if (lease.daysUntilExpiry <= 60 && lease.daysUntilExpiry > 30 && !['reminder_60', 'reminder_30', 'renewal_sent', 'negotiating', 'renewed', 'not_renewing'].includes(currentStatus)) {
      newStatus = 'reminder_60';
    } else if (lease.daysUntilExpiry <= 90 && lease.daysUntilExpiry > 60 && !['reminder_90', 'reminder_60', 'reminder_30', 'renewal_sent', 'negotiating', 'renewed', 'not_renewing'].includes(currentStatus)) {
      newStatus = 'reminder_90';
    }

    if (newStatus && newStatus !== currentStatus) {
      // Update lease renewal_status
      await updateLease(lease.id, { renewal_status: newStatus });

      const tenantName = lease.tenant ? `${lease.tenant.first_name} ${lease.tenant.last_name}` : 'Unknown';
      const propertyLabel = lease.property ? `${lease.property.address}${lease.property.unit ? ', ' + lease.property.unit : ''}` : 'Unknown';
      const daysLabel = newStatus === 'reminder_90' ? '90' : newStatus === 'reminder_60' ? '60' : '30';

      // Create notification
      await createNotification({
        title: `Lease expiring in ${daysLabel} days: ${propertyLabel}`,
        body: `${tenantName}'s lease expires ${lease.lease_end}. Current rent: $${lease.monthly_rent}/mo. Action needed.`,
        notification_type: 'lease_expiring',
        priority: lease.daysUntilExpiry <= 30 ? 'high' : 'normal',
        property_id: lease.property_id,
        contact_id: lease.tenant_id,
      });

      // Log AI activity
      await logAiActivity({
        action_type: 'lease_renewal_reminder',
        description: `${daysLabel}-day lease renewal reminder for ${tenantName} at ${propertyLabel}`,
        property_id: lease.property_id,
        contact_id: lease.tenant_id,
        details: {
          lease_id: lease.id,
          days_until_expiry: lease.daysUntilExpiry,
          monthly_rent: lease.monthly_rent,
          reminder_level: newStatus,
        },
      });

      actions.push({
        lease_id: lease.id,
        tenant: tenantName,
        property: propertyLabel,
        days_until_expiry: lease.daysUntilExpiry,
        action: `Advanced to ${newStatus}`,
        notification_created: true,
      });
    }
  }

  return { actions, error: null };
}

/**
 * Send a lease renewal offer to a tenant.
 * Updates lease status and logs the action.
 */
export async function sendLeaseRenewalOffer(leaseId, proposedRent) {
  const { data: lease, error: fetchError } = await supabase
    .from('leases')
    .select(`
      *,
      property:properties(id, address, unit),
      tenant:contacts!leases_tenant_id_fkey(id, first_name, last_name, phone, email)
    `)
    .eq('id', leaseId)
    .single();

  if (fetchError || !lease) return { data: null, error: fetchError || 'Lease not found' };

  const { data, error } = await updateLease(leaseId, {
    renewal_status: 'renewal_sent',
    renewal_rent: proposedRent,
    renewal_sent_at: new Date().toISOString(),
  });

  if (error) return { data: null, error };

  const tenantName = lease.tenant ? `${lease.tenant.first_name} ${lease.tenant.last_name}` : 'Unknown';
  const propertyLabel = lease.property ? `${lease.property.address}${lease.property.unit ? ', ' + lease.property.unit : ''}` : 'Unknown';

  // Log activity
  await logAiActivity({
    action_type: 'lease_renewal_sent',
    description: `Renewal offer sent to ${tenantName} at ${propertyLabel} — proposed rent: $${proposedRent}/mo`,
    property_id: lease.property_id,
    contact_id: lease.tenant_id,
    details: {
      lease_id: leaseId,
      current_rent: lease.monthly_rent,
      proposed_rent: proposedRent,
    },
  });

  return { data, error: null };
}

/**
 * Update lease renewal response (accepted, counter, declined, no_response)
 */
export async function updateLeaseRenewalResponse(leaseId, response, notes) {
  const statusMap = {
    accepted: 'renewed',
    counter: 'negotiating',
    declined: 'not_renewing',
    no_response: 'renewal_sent', // keep as-is
  };

  const updates = {
    renewal_response: response,
    renewal_status: statusMap[response] || 'renewal_sent',
  };

  if (notes) updates.notes = notes;

  return updateLease(leaseId, updates);
}

// ============================================================
// WEBHOOK: Create lead from Make.com email parser
// Uses the Supabase anon key — called externally
// ============================================================

export async function createLeadFromWebhook(leadData, orgId) {
  const { data, error } = await supabase
    .from('contacts')
    .insert({
      org_id: orgId,
      first_name: leadData.first_name || 'Unknown',
      last_name: leadData.last_name || 'Lead',
      email: leadData.email || null,
      phone: leadData.phone || null,
      lead_status: 'new',
      lead_type: leadData.lead_type || 'buyer',
      lead_source: leadData.lead_source || 'email',
      budget: leadData.budget || null,
      notes: leadData.notes || null,
      roles: [leadData.lead_type === 'renter' ? 'renter' : leadData.lead_type === 'pm_owner' ? 'owner' : 'buyer'],
      tags: ['auto_captured', leadData.lead_source || 'email'].filter(Boolean),
      source_data: leadData.raw_email || {},
    })
    .select()
    .single();
  return { data, error };
}

// ============================================================
// DASHBOARD STATS
// ============================================================

export async function getDashboardStats() {
  const [leads, maintenance, deals, financials] = await Promise.all([
    getLeadPipelineStats(),
    supabase.from('maintenance_requests')
      .select('status, priority')
      .not('status', 'in', '("completed","verified","billed","cancelled")'),
    supabase.from('deals')
      .select('status, deal_type')
      .not('status', 'in', '("closed","fell_through","cancelled")'),
    supabase.from('financial_transactions')
      .select('amount, transaction_type, entity, date')
      .gte('date', new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]),
  ]);

  return {
    leads: leads.data,
    openMaintenance: maintenance.data?.length || 0,
    emergencyMaintenance: maintenance.data?.filter(m => m.priority === 'emergency').length || 0,
    activeDeals: deals.data?.length || 0,
    monthlyIncome: financials.data
      ?.filter(f => ['commission_received', 'rent_collected', 'management_fee', 'late_fee'].includes(f.transaction_type))
      .reduce((sum, f) => sum + Number(f.amount), 0) || 0,
  };
}
